import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { UsageIndicator } from './UsageIndicator';
import { TierBadge } from './TierBadge';
import type { SubscriptionTier } from '@/types';
import { useLimitCheck } from '@/hooks/useLimitCheck';
import { ArrowUpIcon, Cross1Icon } from '@radix-ui/react-icons';
import { cn } from '@/lib/utils';

interface UpgradePromptProps {
  subscriptionTier: SubscriptionTier;
  onUpgrade?: () => void;
  onDismiss?: () => void;
  className?: string;
  variant?: 'default' | 'compact' | 'banner';
}

export function UpgradePrompt({
  subscriptionTier,
  onUpgrade,
  onDismiss,
  className,
  variant = 'default'
}: UpgradePromptProps) {
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const purchaseCheck = useLimitCheck({ actionType: 'purchases', subscriptionTier });
  const listingCheck = useLimitCheck({ actionType: 'listings', subscriptionTier });
  const itemCheck = useLimitCheck({ actionType: 'items', subscriptionTier });

  const hasNearLimits = purchaseCheck.isNearLimit || listingCheck.isNearLimit || itemCheck.isNearLimit;
  const hasReachedLimit = !purchaseCheck.canPerform || !listingCheck.canPerform || !itemCheck.canPerform;

  const handleUpgrade = async () => {
    if (!onUpgrade) return;

    setIsUpgrading(true);
    try {
      await onUpgrade();
    } catch (error) {
      console.error('Upgrade failed:', error);
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (subscriptionTier !== 'FREE' || isDismissed || (!hasNearLimits && !hasReachedLimit)) {
    return null;
  }

  if (variant === 'banner') {
    return (
      <div className={cn(
        'bg-background',
        'border border-secondary/60 rounded-lg p-4',
        className
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-background border border-secondary">
              <ArrowUpIcon className="w-4 h-4 text-secondary" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-secondary">
                  {hasReachedLimit ? 'Limits reached' : 'Approaching limits'}
                </span>
                <TierBadge tier={subscriptionTier} />
              </div>
              <p className="text-sm text-primary-foreground">
                Upgrade to unlock unlimited storage and premium features.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onUpgrade && (
              <Button
                size="sm"
                onClick={handleUpgrade}
                disabled={isUpgrading}
              >
                {isUpgrading ? 'Upgrading...' : 'Upgrade'}
              </Button>
            )}
            {onDismiss && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
              >
                <Cross1Icon className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={cn(
        'flex items-center justify-between p-3 bg-background',
        'border border-secondary rounded-lg',
        className
      )}>
        <div className="flex items-center gap-2">
          <TierBadge tier={subscriptionTier} />
          <span className="text-sm font-medium">
            {hasReachedLimit ? 'Limits reached' : 'Approaching limits'}
          </span>
        </div>
        {onUpgrade && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleUpgrade}
            disabled={isUpgrading}
          >
            {isUpgrading ? 'Upgrading...' : 'Upgrade'}
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card className={cn('border-secondary', className)}>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">
              {hasReachedLimit ? 'Usage limits reached' : 'Approaching usage limits'}
            </h3>
            <TierBadge tier={subscriptionTier} />
          </div>
          {onDismiss && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
            >
              <Cross1Icon className="w-4 h-4" />
            </Button>
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          {hasReachedLimit
            ? 'You\'ve reached your free tier limits. Upgrade to continue adding new items.'
            : 'You\'re approaching your free tier limits. Upgrade now to unlock unlimited storage.'
          }
        </p>

        <div className="space-y-3">
          <UsageIndicator actionType="purchases" subscriptionTier={subscriptionTier} size="sm" />
          <UsageIndicator actionType="listings" subscriptionTier={subscriptionTier} size="sm" />
          <UsageIndicator actionType="items" subscriptionTier={subscriptionTier} size="sm" />
        </div>

        {onUpgrade && (
          <div className="flex justify-end">
            <Button
              onClick={handleUpgrade}
              disabled={isUpgrading}
              className="w-full sm:w-auto"
            >
              {isUpgrading ? 'Upgrading...' : 'Upgrade to Active Plan'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
